import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Wifi, WifiOff, Plus, Save, TestTube, X, Phone, Key, Globe } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/atendimento`;
const headers = () => ({Authorization: `Bearer ${localStorage.getItem('recupnet_token')}`});

export default function AtendimentoWhatsApp() {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [testing, setTesting] = useState('');
  const [saving, setSaving] = useState(false);

  const fetch_ = () => {
    axios.get(`${API}/whatsapp/connections`, {headers: headers()})
      .then(r => setConnections(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => { fetch_(); }, []);

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      if (editing.id && connections.find(c => c.id === editing.id)) {
        await axios.put(`${API}/whatsapp/connections/${editing.id}`, editing, {headers: headers()});
        toast.success('Conexao atualizada');
      } else {
        await axios.post(`${API}/whatsapp/connections`, editing, {headers: headers()});
        toast.success('Conexao criada');
      }
      setEditing(null);
      fetch_();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro');
    } finally { setSaving(false); }
  };

  const testConnection = async (connId) => {
    setTesting(connId);
    try {
      const r = await axios.post(`${API}/whatsapp/connections/${connId}/test`, {}, {headers: headers()});
      if (r.data.connected) {
        toast.success(`Conectado! ${r.data.instanceFound ? 'Instancia encontrada' : 'Instancia nao encontrada'}`);
      } else {
        toast.error(`Falha: ${r.data.error || 'Nao conectado'}`);
      }
      fetch_();
    } catch (err) {
      toast.error('Erro ao testar conexao');
    } finally { setTesting(''); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="rn-spinner" style={{width:28,height:28}}/></div>;

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold">WhatsApp</h1>
          <p className="text-sm text-white/40">Conexoes via Evolution API</p>
        </div>
        <button onClick={() => setEditing({name:'',instanceName:'',apiUrl:'',apiKey:'',phoneNumber:'',businessHours:{start:'08:00',end:'18:00'}})}
          className="btn-primary flex items-center gap-2 text-sm"><Plus size={16}/> Nova Conexao</button>
      </div>

      {/* Connection Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {connections.map(conn => (
          <div key={conn.id} className="glass-card">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                {conn.status === 'connected' ? <Wifi size={18} style={{color:'var(--rn-green)'}}/> : <WifiOff size={18} className="text-red-400"/>}
                <h3 className="text-sm font-semibold">{conn.name}</h3>
              </div>
              <span className={`text-[10px] ${conn.status === 'connected' ? 'badge-success' : 'badge-danger'}`}>
                {conn.status === 'connected' ? 'Conectado' : 'Desconectado'}
              </span>
            </div>
            <div className="space-y-1 text-xs text-white/40 mb-3">
              <p><Phone size={11} className="inline mr-1"/> {conn.phoneNumber || '—'}</p>
              <p><Globe size={11} className="inline mr-1"/> {conn.instanceName || '—'}</p>
              <p><Key size={11} className="inline mr-1"/> {conn.apiUrl ? `${conn.apiUrl.slice(0,30)}...` : '—'}</p>
              {conn.lastSyncAt && <p className="text-[9px] text-white/25">Ultimo sync: {new Date(conn.lastSyncAt).toLocaleString('pt-BR')}</p>}
            </div>
            <div className="flex gap-2">
              <button onClick={() => testConnection(conn.id)} disabled={testing === conn.id}
                className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1">
                {testing === conn.id ? <span className="rn-spinner" style={{width:12,height:12}}/> : <><TestTube size={12}/> Testar</>}
              </button>
              <button onClick={() => setEditing(conn)} className="btn-ghost text-xs py-1.5 px-3">Editar</button>
            </div>
          </div>
        ))}
        {connections.length === 0 && (
          <div className="glass-card col-span-2 text-center py-8">
            <WifiOff size={36} className="mx-auto text-white/15 mb-2"/>
            <p className="text-sm text-white/35">Nenhuma conexao WhatsApp configurada</p>
            <p className="text-xs text-white/20 mt-1">Clique em "Nova Conexao" para configurar a Evolution API</p>
          </div>
        )}
      </div>

      {/* Webhook Info */}
      <div className="glass-card">
        <h3 className="text-sm font-semibold text-white/60 mb-2">Webhook URL</h3>
        <p className="text-xs text-white/40 mb-1">Configure este URL na Evolution API como webhook de recebimento:</p>
        <code className="block p-2 rounded-lg bg-black/30 text-xs font-mono" style={{color:'var(--rn-green)'}}>
          {process.env.REACT_APP_BACKEND_URL}/api/atendimento/webhook/whatsapp/evolution
        </code>
        <p className="text-[10px] text-white/25 mt-2">Eventos suportados: messages.upsert, messages.update, connection.update</p>
      </div>

      {/* Edit Modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-[#111817] border border-white/10 rounded-2xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-semibold">{editing.id ? 'Editar' : 'Nova'} Conexao WhatsApp</h3>
              <button onClick={() => setEditing(null)}><X size={18}/></button>
            </div>
            <div className="space-y-3">
              <Field label="Nome" value={editing.name} onChange={v => setEditing({...editing, name: v})} placeholder="Ex: WhatsApp Principal"/>
              <Field label="Instancia (Evolution)" value={editing.instanceName} onChange={v => setEditing({...editing, instanceName: v})} placeholder="Nome da instancia"/>
              <Field label="API URL" value={editing.apiUrl} onChange={v => setEditing({...editing, apiUrl: v})} placeholder="https://evolution.seudominio.com"/>
              <Field label="API Key" value={editing.apiKey} onChange={v => setEditing({...editing, apiKey: v})} placeholder="Chave de API" type="password"/>
              <Field label="Numero WhatsApp" value={editing.phoneNumber} onChange={v => setEditing({...editing, phoneNumber: v})} placeholder="+5511999999999"/>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Horario Inicio" value={editing.businessHours?.start || '08:00'} onChange={v => setEditing({...editing, businessHours: {...(editing.businessHours||{}), start: v}})} type="time"/>
                <Field label="Horario Fim" value={editing.businessHours?.end || '18:00'} onChange={v => setEditing({...editing, businessHours: {...(editing.businessHours||{}), end: v}})} type="time"/>
              </div>
              <button onClick={save} disabled={saving} className="w-full btn-primary py-2.5 text-sm flex items-center justify-center gap-2">
                {saving ? <span className="rn-spinner" style={{width:14,height:14}}/> : <><Save size={14}/> Salvar</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({label, value, onChange, placeholder, type = 'text'}) {
  return (
    <div>
      <label className="text-xs text-white/40 block mb-1">{label}</label>
      <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="w-full bg-black/20 border border-white/10 text-white text-sm placeholder:text-white/25 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-1 focus:ring-[rgba(99,203,182,0.3)]"/>
    </div>
  );
}
